import {
  LANDING_TESTIMONIAL_MAX_LENGTHS,
  type LandingTestimonialFields,
  landingTestimonialFieldsSchema,
} from '@repo/api-contracts';
import { type ChangeEvent, type FormEvent, type ReactElement, useState } from 'react';
import { z } from 'zod';
import { firstErrorPerField } from '@/shared/lib';
import { Button, FileDropField, Input, Label } from '@/shared/ui';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const FIELD_KEYS = ['title', 'body', 'author'] as const;
const EMPTY_FIELDS: LandingTestimonialFields = { title: '', body: '', author: '' };

type FieldErrors = Partial<Record<keyof LandingTestimonialFields, string>>;

interface LandingTestimonialFormModalProps {
  /** create 는 사진과 글을, edit 는 글만 받는다. */
  readonly mode: 'create' | 'edit';
  /** edit 의 기존 글. create 는 null. */
  readonly initialFields: LandingTestimonialFields | null;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly onSubmit: (fields: LandingTestimonialFields, file: File | null) => void;
  readonly onClose: () => void;
}

/** 기업 고객 사례 등록·수정 모달 — 서버와 같은 계약 스키마로 먼저 검사해 칸별 오류를 보여 준다. */
export const LandingTestimonialFormModal = ({
  mode,
  initialFields,
  isPending,
  errorMessage,
  onSubmit,
  onClose,
}: LandingTestimonialFormModalProps): ReactElement => {
  const [fields, setFields] = useState<LandingTestimonialFields>(initialFields ?? EMPTY_FIELDS);
  const [file, setFile] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [fileError, setFileError] = useState<string | null>(null);
  const title = mode === 'create' ? '기업 고객 사례 추가' : '기업 고객 사례 수정';

  const changeText =
    (key: keyof LandingTestimonialFields) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const { value } = event.target;
      setFields((current) => ({ ...current, [key]: value }));
    };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = landingTestimonialFieldsSchema.safeParse(fields);
    const nextFileError = mode === 'create' && file === null ? '사진을 골라 주세요.' : null;

    setFieldErrors(
      parsed.success
        ? {}
        : firstErrorPerField(z.flattenError(parsed.error).fieldErrors, FIELD_KEYS),
    );
    setFileError(nextFileError);

    if (parsed.success && nextFileError === null) {
      onSubmit(parsed.data, file);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-lg border bg-background p-5"
      >
        <p className="font-medium">{title}</p>

        {errorMessage !== null ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3">
          {mode === 'create' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="testimonial-form-photo">사진</Label>
              <FileDropField
                id="testimonial-form-photo"
                accept={ACCEPT}
                buttonLabel="사진 선택"
                fileName={file?.name ?? null}
                aria-invalid={fileError !== null}
                onFiles={([next]) => setFile(next ?? null)}
              />
              <p className="text-xs text-muted-foreground">
                가로:세로 4:3 권장. 카드 위에 사진이 꽉 차게 나옵니다.
              </p>
              {fileError !== null ? <p className="text-xs text-destructive">{fileError}</p> : null}
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="testimonial-form-title">제목</Label>
            <Input
              id="testimonial-form-title"
              value={fields.title}
              maxLength={LANDING_TESTIMONIAL_MAX_LENGTHS.title}
              placeholder="예: 맞춤 제안이 성공적인 결과로 이어졌어요"
              onChange={changeText('title')}
              aria-invalid={fieldErrors.title !== undefined}
            />
            {fieldErrors.title !== undefined ? (
              <p className="text-xs text-destructive">{fieldErrors.title}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="testimonial-form-body">본문</Label>
            <textarea
              id="testimonial-form-body"
              value={fields.body}
              maxLength={LANDING_TESTIMONIAL_MAX_LENGTHS.body}
              rows={5}
              onChange={changeText('body')}
              aria-invalid={fieldErrors.body !== undefined}
              className="rounded-2xl border border-input bg-background px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-invalid:border-destructive"
            />
            {fieldErrors.body !== undefined ? (
              <p className="text-xs text-destructive">{fieldErrors.body}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="testimonial-form-author">담당자</Label>
            <Input
              id="testimonial-form-author"
              value={fields.author}
              maxLength={LANDING_TESTIMONIAL_MAX_LENGTHS.author}
              placeholder="예: 브랜드 마케터 W님"
              onChange={changeText('author')}
              aria-invalid={fieldErrors.author !== undefined}
            />
            {fieldErrors.author !== undefined ? (
              <p className="text-xs text-destructive">{fieldErrors.author}</p>
            ) : null}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" size="sm" isLoading={isPending}>
              저장
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
